/**
 * A song in the library: cover, title, metadata, playback, secondary actions.
 *
 * Redesigned in the Studio pass from a card into a row. The prototype rendered
 * each song as a tile in a 2–3 column grid: an 84px gradient banner carrying a
 * "Ready" pill and a favourite button, then the title, then a row of five
 * unlabelled icons — play, edit, duplicate, download, delete. Four songs filled
 * the screen, and the five equal-weight glyphs made the library read as a control
 * panel rather than as a list of music.
 *
 * The row keeps the same reading order the tile had — cover, title, metadata,
 * playback, then the rest — but reads it left to right, so a full library scans as
 * a track list. Every action the tile had is still here: Play and Favorite are
 * visible because they are the two you use while scanning, and Edit, Duplicate,
 * Download and Delete moved into a More menu.
 *
 * Two accessibility properties carried over from Phase 1 and still load-bearing:
 *
 *   - The row is role="button" with tabIndex={0} and an Enter/Space handler, not a
 *     real <button>: it contains buttons, and nesting them is invalid HTML that
 *     browsers resolve unpredictably.
 *   - Every control inside stops propagation, so pressing Download does not also
 *     select the row. That was the original Phase 1 defect.
 *
 * CLICKING A ROW NO LONGER OPENS THE STUDIO, which is the correction this pass makes.
 * The row used to be labelled "Open <title> in Lyrixa Studio" and navigate to
 * /library/:songId on any click or Enter — so browsing your own library dropped you into
 * an editing workspace, and the only way to look at a track was to start editing it.
 * Studio is now an explicit destination: "Open in Studio" in the More menu, and nowhere
 * else on the row. What a click does instead is `onSelect` — whatever selection means on
 * the surface drawing the row, which for the Library is opening the lyrics preview, the
 * same thing a Home page card does.
 *
 * Playback is untouched by that change: the accent circle still toggles this song in the
 * one player, and the app's playing waveform sits beside it while this row is the one
 * running — see the PlayingIndicator note at the control.
 */
import {
  Copy,
  Download,
  EyeOff,
  Globe,
  ListPlus,
  Loader2,
  Music,
  Pause,
  Pencil,
  PictureInPicture2,
  Play,
  SlidersHorizontal,
  Star,
  Trash2,
} from 'lucide-react';
import type { ActionMenuItem } from '../ui';
import { ActionMenu, IconButton } from '../ui';
// The files, not the player barrel: this component is reached through songs/index, which the
// player barrel imports for FullscreenPlayer's artwork, so the barrel-to-barrel route would
// close a cycle. See TrendingRow, which reaches for the indicator the same way.
import PlayerBar from '../player/PlayerBar';
import PlayingIndicator from '../player/PlayingIndicator';
import SongArtwork from './SongArtwork';
import { useLiveDateLabel } from '../../hooks/useLiveDateLabel';
import { joinWithMiddleDot } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song, SongStatus } from '../../types/domain';

/*
  STATUS AND VISIBILITY ARE TWO DIFFERENT FACTS, and the row now shows both — but not
  symmetrically, and the asymmetry is the design.

  Status is always drawn: every song has a production state worth reporting. Visibility is
  drawn ONLY when a song is discoverable, because "Private" on every row of a library where
  almost everything is private is a badge that says nothing. Listing a track is the
  exceptional state and the one worth marking, so the indicator appears when there is
  something to indicate. See the badge below.

  Lifted out of the JSX when `draft` gained its own case: three-way ternaries inline read
  as noise, and one line per state is also what src/styles/contrast.test.ts can check.
*/

/** The dot beside the label. Decoration — the word next to it carries the meaning. */
function statusColor(status: SongStatus): string {
  if (status === 'failed') return c.danger;
  // Muted for a draft, because nothing is happening to it: green means either "finished"
  // or "in progress", and a draft is neither.
  if (status === 'draft') return c.text3;
  return c.success;
}

/** One word per status. `draft` used to fall through to "Working", which claimed a
 *  generation was running when none was. */
function statusLabel(status: SongStatus): string {
  if (status === 'ready') return 'Ready';
  if (status === 'failed') return 'Failed';
  if (status === 'draft') return 'Draft';
  return 'Working';
}

export interface SongCardProps {
  song: Song;
  /** True when this song is loaded AND advancing — the Play/Pause label. */
  playing: boolean;
  /**
   * True when this song is the one in the player, running or paused.
   *
   * What the highlight and the scrubber key off, because a paused track is still the
   * one you are on and still has a position worth showing. Defaults to `playing` for
   * callers that have no pause state to report.
   */
  active?: boolean;
  /** Elapsed seconds, used by the inline scrubber while active. */
  elapsed: number;
  /**
   * Playback rate, so the inline scrubber's smoothing matches the tick it is smoothing.
   *
   * Follows `elapsed` down the same chain and for the same reason — both describe the
   * clock, and this card draws the clock. Optional and 1× by default, so a caller with
   * no speed state renders exactly as before.
   */
  speed?: number;
  /**
   * What clicking the row does. NOT "open the Studio" — see the header.
   *
   * Named `onSelect` rather than `onOpen` deliberately: the old name described a
   * destination, and every caller had to pass a Studio navigation to satisfy it. This one
   * describes a gesture and leaves the meaning to the surface — the Library raises the
   * lyrics preview, which is what a song click does on the Home page too.
   */
  onSelect: (song: Song) => void;
  /**
   * Opens this song in Lyrixa Studio. The row's only route there, in the More menu.
   *
   * OPTIONAL, and absent means no Studio item at all rather than a disabled one — the
   * same rule the listing items below follow. A surface showing rows to someone who is
   * not the owner has no business offering the editing workspace, and an item that opened
   * a workspace nobody wired would be worse than no item.
   */
  onOpenInStudio?: (song: Song) => void;
  onTogglePlay: (songId: string) => void;
  onSeek?: (songId: string, seconds: number) => void;
  loading?: boolean;
  /**
   * Plays this song in the mini player — starts it AND raises the floating bar.
   *
   * THE EXPLICIT REQUEST FOR THE BAR, and the reason it is a separate handler from
   * `onTogglePlay` rather than something that surface does automatically. Pressing Play on
   * a row is a request to hear the song, and the row answers it in place: it highlights,
   * it shows the position, its accent circle becomes Pause. A bar arriving over the page
   * for that would be furniture nobody asked for. Someone who DOES want the transport that
   * follows them across the app now says so, here.
   *
   * NOT A SECOND PLAYER. It goes to the same `playerStore` through the same hook, so the
   * song it starts becomes the one active song and this row stops being the active one —
   * see usePlayer's `playInMiniPlayer`.
   *
   * Optional, like the two below, so a surface with no shell to raise a bar in (or one
   * showing rows to someone who is not the owner) renders exactly as it did.
   */
  onPlayInMiniPlayer?: (song: Song) => void;
  onToggleFavorite: (songId: string) => void;
  onEdit: (song: Song) => void;
  onDuplicate: (song: Song) => void;
  onDownload: (song: Song) => void;
  onDelete: (song: Song) => void;
  /** Raises the "Add to playlist" sheet. */
  onAddToPlaylist: (song: Song) => void;
  /**
   * Raises the "Make this song discoverable?" confirmation.
   *
   * Optional, like its counterpart below, so a surface that shows rows without owning a
   * listing decision renders exactly as before. The menu item only appears when the
   * handler is supplied AND the song is ready and private — a control that opened a
   * dialog nobody wired would be worse than no control.
   */
  onMakeDiscoverable?: (song: Song) => void;
  /** Raises the "Remove from Lyrixa Catalogue?" confirmation. */
  onRemoveFromCatalogue?: (song: Song) => void;
}

export default function SongCard({
  song,
  playing,
  active = playing,
  elapsed,
  speed = 1,
  loading = false,
  onSelect,
  onOpenInStudio,
  onTogglePlay,
  onSeek,
  onPlayInMiniPlayer,
  onToggleFavorite,
  onEdit,
  onDuplicate,
  onDownload,
  onDelete,
  onAddToPlaylist,
  onMakeDiscoverable,
  onRemoveFromCatalogue,
}: SongCardProps) {
  const dateLabel = useLiveDateLabel(song.createdAt, song.date);
  /*
    WHICH LISTING ACTION THIS ROW OFFERS, AND WHETHER IT OFFERS ONE AT ALL.

    Ready and private  → Make Discoverable.
    Public             → Remove from Catalogue.
    Anything else      → neither, and that covers two distinct cases:

      A DRAFT, A GENERATION IN FLIGHT OR A FAILURE has nothing to list. There is no
      finished track behind it, so the action is not disabled here — it is absent. A
      greyed-out row in a menu invites a press and then explains itself; an item that is
      not there says the same thing without the detour, and ActionMenuItem has no disabled
      state to reach for anyway.

      UNLISTED is left alone on purpose. It means "reachable by link, listed nowhere", and
      neither of these two actions is the question it wants asked — offering "Make
      Discoverable" would quietly promote a deliberate half-state, so the row shows what it
      is and changes nothing.
  */
  const canMakeDiscoverable =
    Boolean(onMakeDiscoverable) && song.status === 'ready' && song.visibility === 'private';
  const isDiscoverable = song.visibility === 'public';

  /*
    THE EXPLICIT ROUTE INTO THE STUDIO, and the only one on this row.

    An ActionMenuItem rather than a button on the row, because the row already carries the
    two controls you use while scanning — Favorite and Play — and the Studio is not one of
    those: it is a destination you choose, once, for one track. Putting a third visible
    glyph on every row to replace a click that should not have navigated in the first place
    would trade one problem for the control-panel row the redesign moved away from.

    First in the menu, above Rename, because it is the only item that goes somewhere; the
    rest act on the song where it is.
  */
  const studioItems: ActionMenuItem[] = onOpenInStudio
    ? [
        {
          label: 'Open in Studio',
          // Named after the song, like every item here: a screen reader reads a menu item
          // out of the row's context.
          ariaLabel: `Open ${song.title} in Lyrixsa Studio`,
          icon: <SlidersHorizontal size={13} color={c.text2} />,
          onSelect: () => onOpenInStudio(song),
        },
      ]
    : [];

  /*
    "PLAY IN MINI PLAYER" — the one way a song row asks for the floating bar.

    In the menu rather than on the row, for the reason the Studio item above is: the row's
    two visible controls are the two you use while scanning, and this is a deliberate choice
    made once, not something you reach for on every track. It also sits directly beneath
    Open in Studio because the two are the same kind of item — both send this song somewhere
    other than where it is.

    IT IS NOT A DUPLICATE OF THE PLAY BUTTON, and the labels have to make that survivable
    for a screen reader, which hears the item out of the row's context: the accent circle is
    "Play <title>", this is "Play <title> in the mini player". One starts the song where you
    are, the other starts it and brings the transport with it. Both move the same store.
  */
  const miniPlayerItems: ActionMenuItem[] = onPlayInMiniPlayer
    ? [
        {
          label: 'Play in Mini Player',
          ariaLabel: `Play ${song.title} in the mini player`,
          icon: <PictureInPicture2 size={13} color={c.text2} />,
          onSelect: () => onPlayInMiniPlayer(song),
        },
      ]
    : [];

  const listingItems: ActionMenuItem[] = [];
  if (canMakeDiscoverable) {
    listingItems.push({
      label: 'Make Discoverable',
      ariaLabel: `Make ${song.title} discoverable`,
      icon: <Globe size={13} color={c.text2} />,
      onSelect: () => onMakeDiscoverable?.(song),
    });
  } else if (isDiscoverable && onRemoveFromCatalogue) {
    listingItems.push({
      label: 'Remove from Catalogue',
      ariaLabel: `Remove ${song.title} from the Lyrixsa Catalogue`,
      icon: <EyeOff size={13} color={c.text2} />,
      onSelect: () => onRemoveFromCatalogue(song),
    });
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(song)}
      onKeyDown={(e) => {
        // Space is the native activation key for role="button" and would
        // otherwise scroll the page.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(song);
        }
      }}
      /*
        "View", not "Open in Lyrixa Studio", which is the name this row carried while a
        click navigated into the editing workspace. The label has to describe what the
        press does, and it now shows the track — the Studio has its own item in the menu
        with the old name on it.
      */
      aria-label={`View ${song.title}`}
      className="lyx-track-row"
      style={{
        // Highlighted while active — including while paused, because the row you are
        // paused on is still the row you are on, and dropping the highlight would make
        // a pause look like a stop. Obvious in a long list without a second badge.
        border: `1px solid ${active ? c.accentBorder : c.border}`,
        borderRadius: 12,
        background: c.surface1,
        padding: 12,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* SongArtwork, not ArtworkPlaceholder: seeding the gradient on song.id here
            is what made the library ignore artwork applied in the Studio. */}
        <SongArtwork
          song={song}
          height={48}
          radius={8}
          style={{ width: 48, flexShrink: 0, overflow: 'hidden' }}
        >
          <Music
            size={15}
            color="rgba(255,255,255,0.5)"
            style={{ position: 'absolute', bottom: 6, left: 7 }}
          />
        </SongArtwork>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '0 0 3px' }}>
            <p
              style={{
                fontWeight: 500,
                fontSize: 13,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {song.title}
            </p>
            {/* Status as a dot and a word rather than a coloured pill on the
                artwork. Songs are only ever 'ready' in the simulation, so this
                needs to be quiet — but it is the field a real backend will use to
                say 'generating' or 'failed', which is why it stays on the row. */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                color: c.text2,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: statusColor(song.status),
                }}
              />
              {statusLabel(song.status)}
            </span>

            {/* The listing indicator, and only when there is one to give. A globe and
                two words in the accent, matched to the badge beside it in size and
                weight so the row gains a fact rather than a decoration — text2 for the
                words, because this one is read. */}
            {isDiscoverable && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  color: c.text2,
                  flexShrink: 0,
                }}
              >
                <Globe size={9} color={c.accent} aria-hidden="true" />
                Discoverable on Lyrixsa
              </span>
            )}
          </div>
          <p
            style={{
              fontSize: 11,
              color: c.text2,
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {joinWithMiddleDot([song.genre, song.length, dateLabel])}
          </p>
        </div>

        {/* The controls sit inside the clickable row, so each one stops
            propagation to avoid also opening the Studio. */}
        <div
          className="lyx-row-controls"
          style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            label={song.favorite ? `Unfavorite ${song.title}` : `Favorite ${song.title}`}
            stopPropagation
            onClick={() => onToggleFavorite(song.id)}
            style={{ width: 30, height: 30, borderRadius: 8 }}
          >
            <Star
              size={14}
              color={song.favorite ? c.accent : c.text2}
              fill={song.favorite ? c.accent : 'none'}
            />
          </IconButton>

          {/*
            THE APP'S ONE PLAYING VISUAL, and this row is one of the surfaces §13
            standardises. While this song is the playing song the row draws the same
            animated waveform the music bar does — its own seeded shape, four bars,
            rescaled to fit (see lib/waveform.indicatorHeights) — and it goes the moment
            playback pauses, stops or moves to another track. Nothing here watches for
            that: `playing` is `usePlayer().isPlaying(song.id)` and there is one piece of
            state behind it.

            BESIDE THE CONTROL, NOT INSIDE IT (Batch 6 §13). It was drawn inside the accent
            circle, standing in for the Pause glyph, which left the row's primary control
            looking like a chart instead of a pause button. Rendered only while playing,
            because on a row — unlike on the global player bar — there is nothing to hold
            the space for: a stopped row has no waveform, which is the state itself.
          */}
          {playing && <PlayingIndicator seed={song.id} playing />}

          <IconButton
            label={playing ? `Pause ${song.title}` : `Play ${song.title}`}
            stopPropagation
            onClick={() => onTogglePlay(song.id)}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              // The primary action, and the only accent-coloured control in the
              // row. Everything else here is secondary and looks it.
              background: c.accentFill,
              flexShrink: 0,
            }}
          >
            {/* A NORMAL PLAY/PAUSE BUTTON, which is §13's clarification in as many words.
                The glyph says which way the press goes and nothing else; the waveform above
                says whether sound is coming out. */}
            {loading ? (
              <Loader2 size={13} color="#1A1300" className="lyx-spin" />
            ) : playing ? (
              <Pause size={13} color="#1A1300" />
            ) : (
              <Play size={13} color="#1A1300" style={{ marginLeft: 1 }} />
            )}
          </IconButton>

          <ActionMenu
            label={`More actions for ${song.title}`}
            stopPropagation
            triggerStyle={{ width: 30, height: 30 }}
            items={[
              /*
                Studio first, and only when a caller wired it. Spread rather than rendered
                conditionally inline for the reason the listing items below give: `items` is
                mapped and keyed on `label`, so a `false` in the array would need filtering
                at every read.
              */
              ...studioItems,
              // Beside the Studio item, because both send this song somewhere rather than
              // acting on it in place. See the note where it is built.
              ...miniPlayerItems,
              {
                label: 'Rename',
                // Named after the song, because a screen reader reads menu items
                // out of the row's context.
                ariaLabel: `Edit ${song.title}`,
                icon: <Pencil size={13} color={c.text2} />,
                onSelect: () => onEdit(song),
              },
              {
                // Grouped with Rename as the two organising actions, above the two
                // that produce a file and the one that destroys.
                label: 'Add to playlist',
                ariaLabel: `Add ${song.title} to a playlist`,
                icon: <ListPlus size={13} color={c.text2} />,
                onSelect: () => onAddToPlaylist(song),
              },
              /*
                The listing action sits after the two organising ones and before the two
                that produce a file, which is where it belongs by weight: it is a decision
                about the song rather than housekeeping, and it is not destructive. Spread
                rather than conditionally rendered inline so the array holds no holes —
                `items` is mapped and keyed on `label`, and a `false` in the list would
                need filtering at every read.
              */
              ...listingItems,
              {
                label: 'Duplicate',
                ariaLabel: `Duplicate ${song.title}`,
                icon: <Copy size={13} color={c.text2} />,
                onSelect: () => onDuplicate(song),
              },
              {
                label: 'Download',
                ariaLabel: `Download ${song.title}`,
                icon: <Download size={13} color={c.text2} />,
                onSelect: () => onDownload(song),
              },
              {
                label: 'Delete',
                ariaLabel: `Delete ${song.title}`,
                icon: <Trash2 size={13} color={c.danger} />,
                onSelect: () => onDelete(song),
                danger: true,
              },
            ]}
          />
        </div>
      </div>

      {active && (
        <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
          <PlayerBar
            elapsedSec={elapsed}
            lengthStr={song.length}
            onSeek={onSeek ? (seconds) => onSeek(song.id, seconds) : undefined}
            playing={playing}
            speed={speed}
          />
        </div>
      )}
    </div>
  );
}
